// Provider connect flow (FRONT-394, FRONT-985).
//
// We deliberately do NOT use Nango's embedded Connect UI iframe: it loads from
// our connect proxy (…/api/v1/connect), and the provider-login screen renders
// broken there. Instead we open the provider's OAuth directly in a new browser
// tab — still through our own proxy, so Nango stays hidden behind our domain —
// and detect success by polling our own connections list.
//
// Why polling instead of a callback: the integrations service records the new
// connection from Nango's `auth/creation` webhook (keyed to the user/org bound
// at connect-session creation), so the browser never needs the connectionId
// and we don't need a websocket back-channel. Once the connection lands
// server-side, `isConnected()` flips true.
//
// Why the tab is opened synchronously, separate from this flow (FRONT-985):
// a click handler that awaits a network call (the session-token POST) before
// calling window.open loses the browser's "this was a direct user gesture"
// attribution by the time the promise resolves, so the tab gets popup-blocked.
// The caller must call `openConnectTab()` synchronously inside the click
// handler and hand the resulting tab in here; this module only navigates it
// once the OAuth URL is known.

export interface OpenProviderConnectOptions {
  /** The tab opened synchronously by the caller's click handler (see above). */
  tab: PopupHandle;
  /** Connect proxy base, e.g. https://integrations.dev.…/api/v1/connect */
  connectHost: string;
  /** Nango connect-session token, scoped to this user/org + provider. */
  sessionToken: string;
  /** Nango provider unique key (== providerConfigKey on the OAuth route). */
  providerUniqueKey: string;
  /** The org's Nango connection-config params (see `buildConnectOAuthUrl`). */
  connectParams?: Record<string, string>;
  /**
   * Polled until it resolves true (the connection has been recorded) or we
   * give up. Provided by the caller so this module stays free of the API client.
   */
  isConnected: () => Promise<boolean>;
  /**
   * FRONT-2363: the check used once the user has closed the sign-in tab.
   * Defaults to `isConnected`; lets a caller relax or tighten the check for
   * the after-close grace window separately.
   */
  isConnectedAfterClose?: () => Promise<boolean>;
  /** Aborting stops polling immediately, closes the tab, and rejects with 'cancelled'. */
  signal?: AbortSignal;
  /** Test seams / overrides. */
  now?: () => number;
  pollIntervalMs?: number;
  timeoutMs?: number;
  popupCloseGraceMs?: number;
}

/** The slice of Window the connect flow needs (so it's trivially stubbable). */
export interface PopupHandle {
  readonly closed: boolean;
  close: () => void;
  /** Navigates the tab to `url` (implemented over the handle's location). */
  navigate: (url: string) => void;
}

/** How long to keep polling for the connection to appear, in ms. */
const CONNECT_TIMEOUT_MS = 90_000;
/** Gap between connection checks, in ms. */
const POLL_INTERVAL_MS = 2_500;
/**
 * Grace window after the user closes the popup: the provider may have just
 * redirected and the webhook can land a beat later, so we keep checking
 * briefly before treating a closed popup as a cancellation.
 */
const POPUP_CLOSE_GRACE_MS = 8_000;

/**
 * Opens a blank tab synchronously, inside the caller's click handler, before
 * any awaited work happens — this is the whole popup-blocking fix (see the
 * module comment above). No window features → the browser opens a tab, not a
 * popup. `_blank` opens a fresh tab per attempt (we deliberately don't reuse
 * a named tab, so a second click can't hijack an in-progress sign-in).
 * Returns null if the browser blocked it (e.g. called outside a user gesture).
 */
export function openConnectTab(): PopupHandle | null {
  const win = window.open('about:blank', '_blank');
  if (!win) return null;
  // Sever the back-reference before the tab goes cross-origin: otherwise the
  // sign-in page could navigate THIS tab (reverse tabnabbing). Not the
  // `noopener` feature — that makes window.open return null, and the flow
  // needs the handle to navigate and close the tab.
  win.opener = null;
  return {
    get closed() {
      return win.closed;
    },
    close: () => win.close(),
    navigate: (url: string) => {
      win.location.href = url;
    },
  };
}

/**
 * Build the OAuth-start URL on our connect proxy. The proxy strips the
 * `/api/v1/connect` prefix and forwards `/oauth/connect/<key>` to Nango,
 * carrying the connect-session token.
 *
 * `connectParams` are the org's Nango connection-config values, sent as
 * `params[<key>]` (FRONT-1204). Nango interpolates them into the provider's
 * authorize URL, so a provider that needs one and doesn't get it fails with a
 * 400 before the user ever reaches the provider's sign-in page — which is what
 * broke every Microsoft connector: their authorize URL is built around the
 * org's `tenant`. Providers with a static authorize URL (Slack, Google,
 * GitHub) need nothing here, which is why they kept working.
 *
 * Passing them on the URL rather than on the connect session is deliberate and
 * measured: a session created with Nango's own config defaults still 400s on
 * this route in the Nango version we run.
 *
 * `authorization_params[prompt]` is forwarded to the provider's own authorize
 * URL (see `oauthPrompt`). No `scope` is sent: Nango then requests the scopes
 * configured on the platform's integration, the one live list (PDEV-10414).
 */
export function buildConnectOAuthUrl(
  connectHost: string,
  providerUniqueKey: string,
  sessionToken: string,
  connectParams?: Record<string, string>,
): string {
  const base = connectHost.replace(/\/+$/, '');
  const url = new URL(`${base}/oauth/connect/${encodeURIComponent(providerUniqueKey)}`);
  url.searchParams.set('connect_session_token', sessionToken);
  for (const [key, value] of Object.entries(connectParams ?? {})) {
    url.searchParams.set(`params[${key}]`, value);
  }
  url.searchParams.set('authorization_params[prompt]', oauthPrompt(providerUniqueKey));
  return url.toString();
}

/**
 * The OAuth `prompt` value to send for a provider.
 *
 * Without it the provider silently reuses whatever session the browser already
 * has. On Microsoft that lands a non-admin straight on "Need admin approval"
 * with no way to switch identity, because the account was never in question —
 * `select_account` shows the picker first, so the user can sign in with an
 * admin account instead of dead-ending.
 *
 * Google and Zoho get `consent` instead: they only hand back a refresh token
 * when the consent screen is actually shown, so skipping it produces a
 * connection that dies at the first token refresh.
 *
 * Matched on prefix rather than an exhaustive key list so a newly enabled
 * Google connector doesn't silently fall to the wrong default.
 */
export function oauthPrompt(providerUniqueKey: string): 'consent' | 'select_account' {
  const key = providerUniqueKey.toLowerCase();
  return key.startsWith('google') || key.startsWith('zoho') ? 'consent' : 'select_account';
}

/**
 * Sleep that wakes early when `signal` aborts, so a user's Cancel takes
 * effect immediately instead of waiting out the rest of a poll interval.
 * The caller re-checks `signal.aborted` after the sleep and handles it there.
 */
const delay = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });

/**
 * Turns a `useConnectProvider` rejection code into plain, user-facing copy.
 * Shared between the Connections page and the in-chat connect card so the
 * wording never drifts between the two surfaces. `null` means "no error
 * surface" — the caller cancelled deliberately (closed the tab / hit Cancel),
 * which isn't a failure worth showing.
 */
export function friendlyConnectError(code: string): string | null {
  if (code === 'cancelled') return null;
  if (code === 'popup_blocked') {
    return 'Allow popups for this site, then try again.';
  }
  if (code === 'timeout') {
    return "Timed out waiting for the connection — if you finished signing in, refresh; otherwise try again.";
  }
  // FRONT-1279: the sign-in window closed before the connection was recorded.
  // Deliberately NOT null: it may still land, and saying so is the whole point.
  if (code === 'tab_closed') {
    return "We didn't see the sign-in finish. If you did complete it, give it a moment — this page updates itself.";
  }
  if (code === 'not_configured' || code.includes('not_configured')) {
    return "This provider isn't set up for your organisation — ask your admin.";
  }
  // FRONT-2419: the org MCP connect route's codes.
  if (code === 'not_allowed') {
    return "You don't have access to this server — ask your admin.";
  }
  if (code === 'not_found') {
    return 'This server is no longer available — reload the page.';
  }
  if (code === 'upstream_error') {
    return "Couldn't reach the connection service — try again in a moment.";
  }
  return code;
}

/** Options for `awaitConnectInTab`: everything but how the URL was built. */
export type AwaitConnectInTabOptions = Pick<
  OpenProviderConnectOptions,
  'tab' | 'isConnected' | 'isConnectedAfterClose' | 'signal' | 'now' | 'pollIntervalMs' | 'timeoutMs' | 'popupCloseGraceMs'
> & {
  /** The sign-in address to send the pre-opened tab to. */
  url: string;
};

/**
 * Navigates the pre-opened tab to the provider OAuth URL and resolves once
 * the connection is recorded. Rejects with:
 *   - Error('cancelled')  the caller aborted `signal` (the Cancel button) —
 *                         the one verdict that means "show nothing"
 *   - Error('tab_closed') the sign-in tab closed and no connection had been
 *                         recorded a short grace later. It may still land.
 *   - Error('timeout')    no connection appeared within the time budget
 */
export async function openProviderConnect(opts: OpenProviderConnectOptions): Promise<void> {
  return awaitConnectInTab({
    ...opts,
    url: buildConnectOAuthUrl(
      opts.connectHost,
      opts.providerUniqueKey,
      opts.sessionToken,
      opts.connectParams,
    ),
  });
}

/**
 * The tab-and-poll half of a connect, shared by the Providers cards (a Nango
 * URL, above) and org MCP servers (FRONT-2419 — a platform OAuth URL). Same
 * verdicts as `openProviderConnect`; only the sign-in address differs.
 */
export async function awaitConnectInTab(opts: AwaitConnectInTabOptions): Promise<void> {
  const now = opts.now ?? (() => Date.now());
  const pollInterval = opts.pollIntervalMs ?? POLL_INTERVAL_MS;
  const timeout = opts.timeoutMs ?? CONNECT_TIMEOUT_MS;
  const closeGrace = opts.popupCloseGraceMs ?? POPUP_CLOSE_GRACE_MS;
  const url = opts.url;
  const tab = opts.tab;

  const closeTab = () => {
    try {
      if (!tab.closed) tab.close();
    } catch {
      /* cross-origin tabs can throw on close; ignore */
    }
  };

  const cancelled = () => {
    closeTab();
    throw new Error('cancelled');
  };

  if (opts.signal?.aborted) cancelled();
  try {
    tab.navigate(url);
  } catch {
    // The tab can already be gone (the user closed it during the awaited
    // session-token fetch) — setting location on a dead window handle throws.
    // Treat that exactly like a user cancellation, keeping the error contract.
    cancelled();
  }

  const startedAt = now();
  let tabClosedAt: number | null = null;

  for (;;) {
    await delay(pollInterval, opts.signal);

    if (opts.signal?.aborted) cancelled();

    if (await opts.isConnected()) {
      // Connected: close the tab for the user (only ever close on success, so
      // a slow background check never yanks the tab out mid sign-in).
      closeTab();
      return;
    }

    if (opts.signal?.aborted) cancelled();

    // Don't close the tab on timeout — the user may still be signing in.
    if (now() - startedAt > timeout) throw new Error('timeout');
    if (!tab.closed) continue;

    // Tab closed by the user: give the webhook a brief grace to land, else cancel.
    tabClosedAt ??= now();
    if (now() - tabClosedAt <= closeGrace) continue;
    if (await (opts.isConnectedAfterClose ?? opts.isConnected)()) return;
    // FRONT-1279: NOT 'cancelled'. The tab closing means the user is done with
    // it, not that they abandoned the attempt — the connection is recorded from
    // the provider's webhook, which routinely lands after the grace above. The
    // old verdict here rendered nothing at all, so a connection that completed
    // half a second late looked to the user like their click had done nothing.
    // 'cancelled' is now reserved for a deliberate abort (the Cancel button),
    // the only signal that unambiguously means "stop".
    throw new Error('tab_closed');
  }
}
